import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiDownload,
  FiExternalLink,
  FiClock,
  FiMaximize2,
  FiMinimize2,
  FiImage,
  FiFile,
  FiFileText,
  FiCode,
  FiDatabase,
} from "react-icons/fi";
import { formatFileSize, timeAgo, getFileExtension } from "../../../utils/fileUtils";
import "./FilePreviewModal.css";

const FilePreviewModal = ({ file, onClose, onDownload, onViewVersions }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fileType = useMemo(() => {
    const ext = getFileExtension(file.fileName)?.toLowerCase();
    const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"];
    const codeExts = ["js", "jsx", "ts", "tsx", "py", "java", "cpp", "c", "h", "css", "html", "json", "xml"];
    const docExts = ["pdf", "doc", "docx", "txt", "md"];
    const dataExts = ["csv", "xlsx", "xls"];

    if (imageExts.includes(ext)) return "image";
    if (codeExts.includes(ext)) return "code";
    if (docExts.includes(ext)) return "document";
    if (dataExts.includes(ext)) return "data";
    return "other";
  }, [file.fileName]);

  const getFileIcon = () => {
    switch (fileType) {
      case "image":
        return <FiImage size={32} />;
      case "code":
        return <FiCode size={32} />;
      case "document":
        return <FiFileText size={32} />;
      case "data":
        return <FiDatabase size={32} />;
      default:
        return <FiFile size={32} />;
    }
  };

  const handleDownload = () => {
    onDownload?.(file._id);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <motion.div
      className={`rfpm-overlay ${isFullscreen ? "rfpm-overlay--fullscreen" : ""}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className={`rfpm-modal ${isFullscreen ? "rfpm-modal--fullscreen" : ""}`}
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rfpm-header">
          <div className="rfpm-file-info">
            <div className={`rfpm-file-icon rfpm-file-icon--${fileType}`}>
              {getFileIcon()}
            </div>
            <div className="rfpm-file-details">
              <h2 className="rfpm-file-name">{file.fileName}</h2>
              <div className="rfpm-file-meta">
                <span>{formatFileSize(file.fileSize)}</span>
                <span className="rfpm-separator">•</span>
                <span>{timeAgo(file.createdAt)}</span>
                {file.version && (
                  <>
                    <span className="rfpm-separator">•</span>
                    <span className="rfpm-version">v{file.version}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="rfpm-actions">
            <button
              className="rfpm-btn rfpm-btn--icon"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <FiMinimize2 size={18} /> : <FiMaximize2 size={18} />}
            </button>
            <button className="rfpm-btn rfpm-btn--icon" onClick={onClose}>
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Preview Area */}
        <div className="rfpm-preview">
          {fileType === "image" && file.fileUrl ? (
            <div className="rfpm-image-container">
              {loading && (
                <div className="rfpm-loading">
                  <div className="rfpm-spinner"></div>
                </div>
              )}
              <img
                src={file.fileUrl}
                alt={file.fileName}
                className="rfpm-image"
                onLoad={() => setLoading(false)}
                onError={() => {
                  setLoading(false);
                  setError("Failed to load image");
                }}
                style={{ display: loading ? "none" : "block" }}
              />
              {error && (
                <div className="rfpm-error">
                  <FiImage size={40} />
                  <p>{error}</p>
                </div>
              )}
            </div>
          ) : fileType === "document" && file.fileUrl && getFileExtension(file.fileName) === "pdf" ? (
            <div className="rfpm-pdf-container">
              <iframe
                src={file.fileUrl}
                className="rfpm-pdf"
                title={file.fileName}
                onLoad={() => setLoading(false)}
              />
            </div>
          ) : (
            <div className="rfpm-no-preview">
              <div className={`rfpm-no-preview-icon rfpm-no-preview-icon--${fileType}`}>
                {getFileIcon()}
              </div>
              <h3>Preview not available</h3>
              <p>This file type cannot be previewed directly.</p>
              <p className="rfpm-hint">Download the file to view its contents.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="rfpm-footer">
          <div className="rfpm-footer-left">
            {file.description && (
              <p className="rfpm-description">{file.description}</p>
            )}
            {file.tags && file.tags.length > 0 && (
              <div className="rfpm-tags">
                {file.tags.map((tag, index) => (
                  <span key={index} className="rfpm-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="rfpm-footer-actions">
            {onViewVersions && file.version > 1 && (
              <button
                className="rfpm-btn rfpm-btn--secondary"
                onClick={() => onViewVersions(file._id)}
              >
                <FiClock size={16} />
                Version History
              </button>
            )}
            <button className="rfpm-btn rfpm-btn--primary" onClick={handleDownload}>
              <FiDownload size={16} />
              Download
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FilePreviewModal;
